import { useEffect, useRef } from 'react';

import { STREAMERS, type ScheduleItem, type Streamer } from '../data/streamers';

import styles from './StreamerDetailModal.module.css';

interface StreamerDetailModalProps {
  /** 표시할 일정 항목. `null`이면 모달은 닫혀 있다. */
  item: ScheduleItem | null;
  /** 모달 표시 여부. 부모가 관리한다. */
  isOpen: boolean;
  /** 닫기 요청(배경 클릭, ✕ 버튼, ESC 키, `<dialog>`의 cancel 이벤트)에서 호출된다. */
  onClose: () => void;
}

/**
 * 릴레이 방송 카드 클릭 시 열리는 상세 모달.
 *
 * 일반 스트리머 카드는 인물 프로필 이미지 + 이름 + 시간 + 페르소나 설명 + 치지직 바로가기
 * 버튼을 1인용 레이아웃으로 보여준다. 합방 피날레 카드는 3인의 프로필을 가로로 나열하고
 * 각 스트리머의 치지직 채널 링크를 모두 제공한다.
 *
 * 네이티브 `<dialog>` 요소를 사용하여 ESC 키, 포커스 트랩, 백드롭 차단을 브라우저에 위임한다.
 * `prefers-reduced-motion` 환경 대응은 CSS에서 처리.
 */
export function StreamerDetailModal({
  item,
  isOpen,
  onClose,
}: StreamerDetailModalProps): React.JSX.Element | null {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // `<dialog>`의 showModal/close를 isOpen 상태와 동기화한다.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // 모달이 열려 있는 동안 배경(body) 스크롤을 잠근다.
  // 네이티브 `<dialog>`가 클릭은 차단하지만, 휠/터치 스크롤은 그대로 흘러가므로
  // 직접 `overflow: hidden`을 걸어 주어야 모달 뒤가 움직이지 않는다.
  // 컴포넌트 언마운트 또는 모달 닫힘 시 원래 값을 그대로 복원한다.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (item === null) {
    return null;
  }

  const isFinale = item.id === 'finale';

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClose={onClose}
      aria-labelledby="streamer-modal-title"
    >
      <button
        type="button"
        aria-label="닫기"
        className={styles.backdrop}
        onClick={onClose}
        tabIndex={-1}
      />
      <div className={styles.inner}>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="닫기"
          onClick={onClose}
        >
          ✕
        </button>

        {isFinale ? <FinaleBody /> : <StreamerBody streamer={item} />}
      </div>
    </dialog>
  );
}

/**
 * 단일 스트리머 카드용 모달 본문.
 *
 * 큰 프로필 이미지 + 이름 + 시간 + 페르소나 설명 + 치지직 바로가기 버튼.
 * 채널 URL이 비어 있으면 버튼 자리를 "치지직 채널 준비 중" 안내로 대체한다.
 */
function StreamerBody({ streamer }: { streamer: Streamer }): React.JSX.Element {
  return (
    <article className={styles.body}>
      <img
        src={streamer.profileImageUrl}
        alt={`${streamer.name} 프로필 이미지`}
        className={styles.profileImage}
      />
      <h2 id="streamer-modal-title" className={styles.title}>
        {streamer.name}
      </h2>
      <p className={styles.timeRow}>
        {formatTime(streamer.startAt)} – {formatTime(streamer.endAt)}
      </p>
      <p className={styles.persona}>{streamer.persona}</p>
      <ChzzkLink channelUrl={streamer.channelUrl} streamerName={streamer.name} />
    </article>
  );
}

/**
 * 합방 피날레 카드용 모달 본문.
 *
 * 3인의 프로필을 가로로 펼쳐 보여주고, 각 인물별 치지직 채널 링크를 한 번에 노출한다.
 */
function FinaleBody(): React.JSX.Element {
  return (
    <article className={styles.body}>
      <h2 id="streamer-modal-title" className={styles.title}>
        3인 합방 피날레
      </h2>
      <div className={styles.finaleGrid}>
        {STREAMERS.map((streamer) => (
          <div key={streamer.id} className={styles.finaleCell}>
            <img
              src={streamer.profileImageUrl}
              alt={`${streamer.name} 프로필 이미지`}
              className={styles.finaleProfileImage}
            />
            <p className={styles.finaleName}>{streamer.name}</p>
            <ChzzkLink
              channelUrl={streamer.channelUrl}
              streamerName={streamer.name}
              compact
            />
          </div>
        ))}
      </div>
    </article>
  );
}

/**
 * 치지직 채널 바로가기 버튼.
 *
 * `channelUrl`이 비어 있으면(아직 미정인 경우) 안내 문구로 표시되고 클릭은 비활성화된다.
 * `compact` 모드는 피날레 그리드 안의 작은 버튼용.
 */
function ChzzkLink({
  channelUrl,
  streamerName,
  compact = false,
}: {
  channelUrl: string | null;
  streamerName: string;
  compact?: boolean;
}): React.JSX.Element {
  const className = [styles.chzzkLink, compact ? styles.chzzkLinkCompact : '']
    .filter(Boolean)
    .join(' ');

  if (channelUrl === null || channelUrl === '') {
    return (
      <span className={[className, styles.chzzkLinkDisabled].join(' ')} aria-disabled>
        🔗 치지직 채널 준비 중
      </span>
    );
  }

  return (
    <a
      href={channelUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={`${streamerName} 치지직 채널로 이동 (새 탭)`}
    >
      🔗 치지직 바로가기
    </a>
  );
}

/**
 * ISO 시각을 KST `HH:mm` 형식으로 포맷.
 */
function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
